import React, { useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  LabelList,
  Tooltip,
} from 'recharts';
import _ from 'lodash';
import moment from 'moment';

import { storeDashboardAnalysis } from '../service/widget.service';
import { formatDateTime, formatTimeHM } from '../../../../utils/Helpers';

const StackedBarChart = ({
  cubeResponse,
  annotation,
  dashboardDetails,
  dashboardLogDetails,
  valueAnnotation,
}) => {
  const [hiddenKeys, setHiddenKeys] = useState([]);
  const truncate = (text, width) =>
    text && text.length > width ? text.slice(0, width) + '…' : text || '';

  const formatWithValueAnnotation = value => {
    let formatter = null;
    if (valueAnnotation) {
      Object.keys(valueAnnotation).forEach(key => {
        if (valueAnnotation?.[key]?.meta?.isDateTimeField) {
          return formatDateTime(`${value}Z`);
        } else if (valueAnnotation[key]?.meta?.isTimeField) {
          return formatTimeHM(`${value}Z`);
        } else if (valueAnnotation?.[key]?.meta?.format) {
          formatter = eval(valueAnnotation[key].meta.format);
        }
      });
    }
    return formatter ? formatter(value) : value;
  };
  const getMeasureKey = row => {
    const priorityKeys = Object.keys(row).filter(k =>
      /(sum|count|min|max|avg)/i.test(k)
    );
    if (priorityKeys.length) return priorityKeys[0]; // pick first priority key

    return Object.keys(row).find(k => !isNaN(Number(row[k])));
  };
  const formatWithAnnotation = (annot, key, formatValue) => {
    let formatter = null;

    if (formatValue == null) {
      return '';
    } else if (annot?.[key]?.meta?.isDateTimeField) {
      return formatDateTime(`${formatValue}Z`);
    } else if (annotation[key]?.meta?.isTimeField) {
      return formatTimeHM(`${formatValue}Z`);
    } else if (annotation[key]?.type === 'time') {
      return moment(`${formatValue}`).format('MM-DD-YYYY');
    } else if (annot) {
      formatter = annot?.[key]?.meta?.format
        ? eval(annot[key].meta.format)
        : null;
    }

    return formatter ? formatter(formatValue) : formatValue;
  };
  const chartData = useMemo(() => {
    if (!cubeResponse?.length) return [];
    const grouped = {};

    cubeResponse.forEach(row => {
      const clonedRow = _.cloneDeep(row);
      const keys = Object.keys(clonedRow);

      const measureKey = getMeasureKey(clonedRow);
      if (!measureKey) return;

      const dimensionKeys = keys.filter(k => k !== measureKey);
      if (dimensionKeys.length < 2) return;

      const groupKey = dimensionKeys[0];
      const stackKey = dimensionKeys[1];

      const groupValue = clonedRow[groupKey] ?? 'Unknown';

      const numericValue =
        typeof clonedRow[measureKey] === 'number'
          ? clonedRow[measureKey]
          : +Number(clonedRow[measureKey]).toFixed(3);

      if (!grouped[formatWithAnnotation(annotation, groupKey, groupValue)])
        grouped[formatWithAnnotation(annotation, groupKey, groupValue)] = {
          name: formatWithAnnotation(annotation, groupKey, groupValue),
        };

      const stackValue = clonedRow[stackKey];
      grouped[formatWithAnnotation(annotation, groupKey, groupValue)][
        formatWithAnnotation(annotation, stackKey, stackValue)
      ] =
        (grouped[formatWithAnnotation(annotation, groupKey, groupValue)][
          formatWithAnnotation(annotation, stackKey, stackValue)
        ] || 0) + numericValue;
    });

    const allStackKeys = Array.from(
      new Set(
        cubeResponse.flatMap(row =>
          Object.keys(row).filter(
            k => k !== getMeasureKey(row) && k !== Object.keys(row)[0]
          )
        )
      )
    );

    Object.values(grouped).forEach(row => {
      allStackKeys.forEach(key => {
        if (!(key in row)) row[key] = 0;
      });
    });

    return Object.values(grouped);
  }, [cubeResponse, annotation]);

  const stackKeys = useMemo(() => {
    const keys = new Set();

    cubeResponse?.forEach(row => {
      const measureKey = getMeasureKey(row);
      const dimensionKeys = Object.keys(row).filter(k => k !== measureKey);
      if (dimensionKeys.length < 2) return;

      const stackKey = dimensionKeys[1];
      const rawValue = row[stackKey];

      const formattedValue = formatWithAnnotation(
        annotation,
        stackKey,
        rawValue
      );
      keys.add(formattedValue);
    });

    return Array.from(keys);
  }, [cubeResponse, annotation]);

  if (dashboardDetails && dashboardLogDetails) {
    storeDashboardAnalysis({
      ...dashboardLogDetails,
      ...dashboardDetails,
      executionTime: new Date().getTime() - dashboardDetails.start_time,
      end_time: new Date().getTime(),
    });
  }

  const showLabels = chartData.length <= 25;
  const MIN_SEGMENT_HEIGHT_FOR_LABEL = 20;

  const renderLegend = (props, hiddenKeys, toggle) => {
    const { payload } = props;

    return (
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: '12px',
          paddingBottom: '10px',
        }}
      >
        {payload.map((entry, index) => {
          const isHidden = hiddenKeys.includes(entry.dataKey);

          // Legend labels are stack dimension keys (e.g. "$ 50.00"), not measure values.
          const formattedValue = entry.dataKey ?? entry.value;
          return (
            <span
              key={`item-${index}`}
              onClick={e => toggle(entry.dataKey, e)}
              style={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontWeight: 500,
                textDecoration: isHidden ? 'line-through' : 'none',
                opacity: isHidden ? 0.5 : 1,
              }}
            >
              <span
                style={{
                  width: 12,
                  height: 12,
                  backgroundColor: entry.color,
                  display: 'inline-block',
                  borderRadius: 2,
                }}
              />
              {formattedValue}
            </span>
          );
        })}
      </div>
    );
  };

  const toggleLegend = (key, e) => {
    e.stopPropagation();
    e.preventDefault();

    setHiddenKeys(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const generatePastelColor = key => {
    let hash = 0;

    for (let i = 0; i < key?.toString().length; i++) {
      hash = key.toString().charCodeAt(i) + ((hash << 5) - hash);
    }

    const r = (hash >> 0) & 255;
    const g = (hash >> 8) & 255;
    const b = (hash >> 16) & 255;

    const pastel = v => Math.floor((v + 255) / 2);

    return `rgb(${pastel(r)}, ${pastel(g)}, ${pastel(b)})`;
  };

  const colors = useMemo(() => {
    const map = {};
    stackKeys.forEach(key => {
      map[key] = generatePastelColor(key);
    });
    return map;
  }, [stackKeys]);

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart
        data={chartData}
        margin={{ top: 40, right: 20, left: 0, bottom: 5 }}
        barCategoryGap="20%"
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
        <XAxis dataKey="name" />
        <YAxis />

        <Legend
          verticalAlign="top"
          align="center"
          content={props => renderLegend(props, hiddenKeys, toggleLegend)}
        />

        <Tooltip
          formatter={(value, name) => [formatWithValueAnnotation(value), name]}
        />

        {stackKeys.map(key => (
          <Bar
            key={key}
            dataKey={key}
            stackId="a"
            fill={colors[key]}
            stroke="#fff"
            strokeWidth={1}
            hide={hiddenKeys.includes(key)}
            margin={{ top: 70, right: 20, left: 0, bottom: 0 }}
          >
            {showLabels && (
              <LabelList
                dataKey={key}
                content={props => {
                  const { x, y, width, height, value } = props;

                  if (value == null || height < MIN_SEGMENT_HEIGHT_FOR_LABEL)
                    return null;

                  const formattedValue = formatWithValueAnnotation(value);

                  const label = truncate(formattedValue, 5);

                  return (
                    <text
                      x={x + width / 2}
                      y={y + height / 2}
                      fill="#000"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      style={{ fontSize: 12, fontWeight: 500 }}
                    >
                      {label}
                    </text>
                  );
                }}
              />
            )}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default StackedBarChart;
